// Port of ios/Sources/Models/PostSeenDetails.swift + ChannelViewModel.withLocalNames — the postseen
// plugin's per-post drill-in (`GET /plugins/com.satoritech.postseen/post/{id}/details`,
// shared/PLUGINS.md §2): who saw one of YOUR messages and when, and who has not yet. Seen first,
// pending after (SeenBySheet), in the server's order.

export interface SeenUser {
  userId: string;
  username: string;
  displayName: string;
  /** Epoch millis; 0 for unseen users (the field is omitted on the wire for them). */
  seenAt: number;
}

export interface PostSeenDetails {
  postId: string;
  audienceCount: number;
  seenCount: number;
  seenUsers: SeenUser[];
  unseenUsers: SeenUser[];
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}

function users(raw: unknown): SeenUser[] {
  if (!Array.isArray(raw)) return [];
  const out: SeenUser[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const u = item as Record<string, unknown>;
    const userId = str(u.user_id);
    if (!userId) continue; // `user_id` is the one required key in the Swift decoder
    out.push({ userId, username: str(u.username), displayName: str(u.display_name), seenAt: num(u.seen_at) });
  }
  return out;
}

/** The plugin's answer; null when `post_id` is missing (the Swift decode throws there). */
export function decode(raw: unknown): PostSeenDetails | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const postId = str(r.post_id);
  if (!postId) return null;
  return {
    postId,
    audienceCount: num(r.audience_count),
    seenCount: num(r.seen_count),
    seenUsers: users(r.seen_users),
    unseenUsers: users(r.unseen_users),
  };
}

/**
 * The plugin computes each row's `display_name` under its own idea of how people are named. Wherever
 * the user is already in the local store the name is recomputed under the reader's policy; the
 * plugin's string stays as the fallback for anyone not synced yet.
 */
export function withLocalNames(details: PostSeenDetails, localName: (userId: string) => string | null): PostSeenDetails {
  const rename = (list: SeenUser[]) =>
    list.map((u) => {
      const name = localName(u.userId);
      return name ? { ...u, displayName: name } : u;
    });
  return { ...details, seenUsers: rename(details.seenUsers), unseenUsers: rename(details.unseenUsers) };
}

/** The row's name: the display name, else `@username` (SeenBySheet.row). */
export function rowName(user: SeenUser): string {
  return user.displayName || '@' + user.username;
}

export const PostSeenDetailsCodec = { decode, withLocalNames, rowName } as const;
